// React
import { useEffect } from "react"

// Constants
import { UserRoles } from "@/constants/constants"

// GraphQL
import {
	MangopayScaConsentState,
	ProjectStateEnum,
	Role,
	useUserContextQuery,
	UsersUserMangopayRecipientStatusChoices,
	type KycType,
	type UsersUserProfileIdentityTypeChoices,
} from "@/api/graphql"
import { GraphQLError } from "graphql"

// Sentry
import { setSentryUser } from "@/lib/sentry"

// Freshdesk
import { setFreshdeskUser } from "@/lib/feedback"

// Feature Flags
import { featureFlags } from "@/lib/featureFlags"

// Dates
import { transformTimezoneConstantToJSTimezone } from "@/lib/dates"

const roleMap: Record<Role, UserRoles> = {
	[Role.Admin]: UserRoles.admin,
	[Role.Investor]: UserRoles.investor,
	[Role.Installer]: UserRoles.installer,
	[Role.ProjectOwner]: UserRoles.projectOwner,
	[Role.EnergySupplier]: UserRoles.energyProvider,
}

export function useFeatureFlags() {
	function getFeatureFlagValue(name: string) {
		return Boolean(featureFlags.find((flag) => flag.name === name)?.value)
	}

	return {
		featureFlags,
		getFeatureFlagValue,
	}
}

/**
 * Use current user ID
 *
 * This is used to check if the user is loggedIn.
 * Therefore, we are not handling the error thats returned 'UserNotLoggedIn'
 * but instead passes it to AuthProvider which handles this case.
 *
 * @returns
 */
export function useCurrentUserId() {
	const { data, error } = useUserContextQuery(undefined, {
		retry: false,
		throwOnError: false,
	})
	return {
		id: data?.me?.id,
		error: error as GraphQLError[],
	}
}

// Use current user info
export function useCurrentUserInfo() {
	const { data } = useUserContextQuery()
	return {
		first_name: data?.me?.first_name ?? "",
		last_name: data?.me?.last_name ?? "",
	}
}

/**
 * Use current user roles
 *
 * NOTE: This function also requires us to disable the error boundary because this is checked in the router layer and it might return UserNotLoggedIn!
 *!
 * @returns
 */
export function useCurrentUserRoles() {
	const { data } = useUserContextQuery(undefined, {
		retry: false,
		throwOnError: false,
	})

	const userRoles = new Set<UserRoles>()

	if (data?.me?.roles) {
		for (const role of data.me.roles) {
			if (role && roleMap[role]) {
				userRoles.add(roleMap[role])
			}
		}
	}

	// Set user_id for analytics — only when identity fields change
	useEffect(() => {
		if (!data?.me?.id) return

		setSentryUser({
			id: data.me.id,
			name: data.me.full_name ?? "",
			email: data.me.email,
		})

		setFreshdeskUser({
			name: data.me.full_name ?? "",
			email: data.me.email,
		})

		if (typeof window.gtag !== "undefined") {
			window.gtag("set", {
				user_id: data.me.id,
			})
		}
	}, [data?.me?.id, data?.me?.full_name, data?.me?.email])

	function hasRole(role: UserRoles) {
		return userRoles.has(role)
	}

	function hasRoles(...roles: Array<UserRoles>) {
		return roles.every((role) => hasRole(role))
	}

	function hasOneOfRoles(...roles: Array<UserRoles>) {
		return roles.find((role) => hasRole(role))
	}

	return {
		userRoles,
		hasRoles,
		hasRole,
		hasOneOfRoles,
	}
}

export function useCurrentUserIdentityType() {
	const { data } = useUserContextQuery()
	return data?.me?.profile
		?.identity_type as UsersUserProfileIdentityTypeChoices
}

// Return current user kyc info
export function useCurrentUserKyc() {
	const { data } = useUserContextQuery()
	return data?.me?.kyc as KycType
}

// Return current user valid bank details
//
// "Valid" means: an IBAN is on file AND its Mangopay Recipient is ACTIVE,
// i.e. SCA has been completed and Mangopay will accept payouts. PENDING
// (mid-SCA), CANCELED (abandoned/failed) and DEACTIVATED recipients all
// fail the check.
export function useCurrentHasValidBankDetails() {
	const { data } = useUserContextQuery()
	const hasIban = data?.me?.investor?.masked_iban != null
	const recipientActive =
		data?.me?.mangopay_sca?.recipient_status ===
		UsersUserMangopayRecipientStatusChoices.Active
	return hasIban && recipientActive
}

// Return current user Mangopay status
export function useCurrentUserMangoPayStatus(): boolean {
	const { data } = useUserContextQuery()
	return data?.me?.mangopay?.sca_enrolled ?? false
}

// All Mangopay SCA consents applicable to our platform must be ACTIVE for the
// platform to work end-to-end (contact info, view account, recipient
// registration, transfer). NOT_APPLICABLE means Mangopay has not activated
// that scope for us and the row is ignored; INACTIVE means the user has
// revoked or never granted it and blocks payouts/investments.
export function useMangopayConsentStatus() {
	const { data } = useUserContextQuery()
	const scaData = data?.me?.mangopay_sca
	const applicableConsents = [
		scaData?.sca_consent_contact_information,
		scaData?.sca_consent_view_account_information,
		scaData?.sca_consent_recipient_registration,
		scaData?.sca_consent_transfer,
	].filter((state) => state !== MangopayScaConsentState.NotApplicable)
	const hasScaData = Boolean(scaData)
	const allGranted =
		hasScaData &&
		applicableConsents.every(
			(state) => state === MangopayScaConsentState.Active,
		)
	return {
		hasScaData,
		allGranted,
		missingCount: applicableConsents.filter(
			(state) => state !== MangopayScaConsentState.Active,
		).length,
	}
}

// Get the current users language and timezone
export function useCurrentUserLocale() {
	const { data } = useUserContextQuery()
	return {
		language: data?.me?.profile?.language,
		timezone: transformTimezoneConstantToJSTimezone(
			data?.me?.profile?.timezone,
		),
	}
}

// Users payouts count
export function useCurrentUserPayoutsCount() {
	const { data } = useUserContextQuery()
	return data?.me?.payment_entries?.totalCount ?? 0
}

// Users Provisional Investments
export function useCurrentUserProvisonalInvestmentsCount() {
	const { data } = useUserContextQuery()
	return data?.me?.provisional_investments?.totalCount ?? 0
}

// Users Investments
export function useCurrentUserInvestmentsCount() {
	const { data } = useUserContextQuery()
	return data?.me?.investment_projects?.totalCount ?? 0
}

// Count Users Investments who have InProduction state
export function useCurrentUserInvestmentsInProductionCount() {
	const { data } = useUserContextQuery()
	return (
		data?.me?.investment_projects?.results?.filter(
			(project) => project?.state === ProjectStateEnum.InProduction,
		)?.length ?? 0
	)
}
